import { Container, Graphics, Rectangle, type FederatedPointerEvent } from 'pixi.js';
import gsap from 'gsap';
import { COLORS, FONT, SCREEN, SHAPE } from '../config/tokens';
import type { Layout, Screen } from '../render/stage';
import { button, iconButton, label, titleChip } from '../ui/components';
import type { Nav } from '../meta/nav';
import { LEVELS } from '../levels';
import { Rng } from '../core/rng';
import { reducedMotion } from '../render/fx';

const STEP = 190; // vertical distance between level nodes
const PARIS_FROM = 19;

/** Node position of a level (map content coordinates, level 1 at the bottom). */
function nodePos(id: number): { x: number; y: number } {
  return { x: SCREEN.width / 2 + 250 * Math.sin((id - 1) * 0.75), y: -(id - 1) * STEP };
}

/**
 * Mockup 30: illustrative travel map (not geographic). Drawn in code – the
 * island, path, trees, café and Eiffel tower are simple shapes until map
 * art exists. Drag to scroll; tap an open level to play it.
 */
export class WorldMapScreen implements Screen {
  readonly view = new Container();
  private readonly sea = new Graphics();
  private readonly map = new Container();
  private readonly header = new Container();
  private readonly footer = new Container();
  private l?: Layout;
  private minY = 0;
  private maxY = 0;
  private drag?: { y: number; mapY: number; moved: number; t: number };
  private velocity = 0;

  constructor(private readonly nav: Nav) {
    this.buildMap();
    const back = iconButton('back', () => nav.showCafe());
    back.position.set(100, 0);
    const title = titleChip('World Trip');
    title.position.set(SCREEN.width / 2, 0);
    this.header.addChild(back, title);
    const next = Math.min(nav.save().nextLevel, LEVELS.length);
    const play = button(`Play Level ${next}`, 600, 'primary', () => nav.playLevel(next), 104);
    play.position.set(SCREEN.width / 2, 0);
    this.footer.addChild(play);
    this.view.addChild(this.sea, this.map, this.header, this.footer);

    this.view.eventMode = 'static';
    this.view.on('pointerdown', (e) => this.down(e));
    this.view.on('globalpointermove', (e) => this.move(e));
    this.view.on('pointerup', () => this.up());
    this.view.on('pointerupoutside', () => this.up());
  }

  private buildMap(): void {
    const save = this.nav.save();
    const last = LEVELS.length;
    const rng = new Rng(7);
    const land = new Graphics();
    // island: soft blobs along the path; the Paris part in lavender
    for (let id = 0; id <= last + 1; id += 0.5) {
      const p = nodePos(id);
      const paris = id >= PARIS_FROM - 0.5;
      land.circle(p.x + (rng.next() - 0.5) * 80, p.y, 300 + rng.next() * 60).fill(paris ? COLORS.lavender : COLORS.sage);
    }
    land.circle(SCREEN.width / 2, 260, 330).fill(COLORS.sage);
    this.map.addChild(land);

    // trees, away from the path
    const trees = new Graphics();
    for (let id = 1; id <= last; id++) {
      const p = nodePos(id);
      for (const side of [-1, 1]) {
        if (rng.next() < 0.45) continue;
        const x = p.x + side * (170 + rng.next() * 90);
        if (x < 40 || x > SCREEN.width - 40) continue;
        const y = p.y + (rng.next() - 0.5) * 120;
        const paris = id >= PARIS_FROM;
        trees.rect(x - 7, y, 14, 34).fill(COLORS.wood);
        trees.circle(x, y - 6, 34).fill(paris ? COLORS.blush : COLORS.sage).stroke({ width: 4, color: COLORS.cocoa });
      }
    }
    this.map.addChild(trees);

    // path: cocoa edge, cream road
    const road = new Graphics();
    const pts = [{ x: SCREEN.width / 2, y: 200 }, ...LEVELS.map((l) => nodePos(l.id))];
    for (const [w, c] of [[46, COLORS.cocoa], [36, COLORS.cream]] as const) {
      road.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) {
        const a = pts[i - 1];
        const b = pts[i];
        road.quadraticCurveTo(a.x, (a.y + b.y) / 2, b.x, b.y);
      }
      road.stroke({ width: w, color: c, cap: 'round', join: 'round' });
    }
    this.map.addChild(road);

    this.map.addChild(this.house(), this.eiffel(save.nextLevel > 18));
    const tokyo = titleChip('Tokyo · coming soon', 380);
    tokyo.scale.set(0.7);
    tokyo.position.set(SCREEN.width / 2, nodePos(last).y - 220);
    this.map.addChild(tokyo);

    for (const l of LEVELS) this.map.addChild(this.node(l.id, save.nextLevel, save.bestStars[l.id] ?? 0));
    this.map.addChild(this.plane(save.nextLevel));
    this.minY = nodePos(last).y - 320;
    this.maxY = 420;
  }

  private node(id: number, next: number, stars: number): Container {
    const c = new Container();
    const p = nodePos(id);
    c.position.set(p.x, p.y);
    const open = id <= next || this.nav.debug;
    const current = id === next;
    const g = new Graphics();
    const r = current ? 54 : 44;
    g.circle(0, 5, r).fill(COLORS.cocoa);
    g.circle(0, 0, r).fill(current ? COLORS.raspberry : open ? COLORS.cream : COLORS.latte).stroke({ width: SHAPE.outline, color: COLORS.cocoa });
    const n = label(String(id), current ? 42 : 34, { weight: FONT.weight.heading, color: current ? 'cream' : open ? 'cocoa' : 'wood' });
    c.addChild(g, n);
    if (!open) {
      // small lock below the number
      const lock = new Graphics().roundRect(-11, 20, 22, 17, 4).fill(COLORS.wood).moveTo(-7, 21).arc(0, 20, 7, Math.PI, 0).stroke({ width: 4, color: COLORS.wood });
      n.y = -8;
      c.addChild(lock);
    } else if (!current && stars > 0) {
      for (let i = 0; i < 3; i++) {
        const s = new Graphics().star((i - 1) * 30, 58, 5, 14, 6).fill(i < stars ? COLORS.butter : COLORS.latte).stroke({ width: 3, color: COLORS.cocoa });
        c.addChild(s);
      }
    }
    if (current) {
      const marker = new Graphics().poly([-20, -96, 20, -96, 0, -68]).fill(COLORS.butter).stroke({ width: SHAPE.outline, color: COLORS.cocoa });
      c.addChild(marker);
      if (!reducedMotion) gsap.to(marker, { y: -10, duration: 0.6, yoyo: true, repeat: -1, ease: 'sine.inOut' });
    }
    if (open) {
      c.eventMode = 'static';
      c.cursor = 'pointer';
      c.on('pointertap', () => {
        if (this.drag && this.drag.moved > 12) return;
        this.nav.playLevel(id);
      });
    }
    return c;
  }

  /** The café as "Home" at the start of the path. */
  private house(): Container {
    const c = new Container();
    const g = new Graphics();
    g.rect(-110, -90, 220, 150).fill(COLORS.cream).stroke({ width: SHAPE.outline, color: COLORS.cocoa });
    g.poly([-130, -90, 0, -170, 130, -90]).fill(COLORS.wood).stroke({ width: SHAPE.outline, color: COLORS.cocoa });
    for (let i = 0; i < 6; i++) g.circle(-100 + i * 40, -86, 20).fill(i % 2 ? COLORS.cream : COLORS.raspberry).stroke({ width: 3, color: COLORS.cocoa });
    g.roundRect(-26, -20, 52, 80, 22).fill(COLORS.wood).stroke({ width: 3, color: COLORS.cocoa });
    g.rect(-90, -40, 44, 40).fill(COLORS.sky).stroke({ width: 3, color: COLORS.cocoa });
    g.rect(46, -40, 44, 40).fill(COLORS.sky).stroke({ width: 3, color: COLORS.cocoa });
    const home = titleChip('Home', 200);
    home.scale.set(0.6);
    home.y = 110;
    c.addChild(g, home);
    c.position.set(SCREEN.width / 2, 240);
    return c;
  }

  /** Simple Eiffel tower marking the start of Paris. */
  private eiffel(unlocked: boolean): Container {
    const c = new Container();
    const g = new Graphics();
    g.poly([-70, 0, -12, -230, 12, -230, 70, 0, 40, 0, 0, -120, -40, 0]).fill(COLORS.wood).stroke({ width: 4, color: COLORS.cocoa });
    g.rect(-44, -95, 88, 14).fill(COLORS.wood).stroke({ width: 3, color: COLORS.cocoa });
    g.rect(-26, -170, 52, 12).fill(COLORS.wood).stroke({ width: 3, color: COLORS.cocoa });
    g.rect(-3, -270, 6, 44).fill(COLORS.cocoa);
    const chip = titleChip(unlocked ? 'Paris' : 'Paris · next stop', 240);
    chip.scale.set(0.62);
    chip.y = 40;
    c.addChild(g, chip);
    const p = nodePos(PARIS_FROM);
    const q = nodePos(PARIS_FROM - 1);
    c.position.set(p.x > SCREEN.width / 2 ? 220 : SCREEN.width - 220, (p.y + q.y) / 2 + 60);
    return c;
  }

  /** Little plane circling near the current level. */
  private plane(next: number): Container {
    const c = new Container();
    const g = new Graphics();
    g.ellipse(0, 0, 46, 13).fill(COLORS.cream).stroke({ width: 4, color: COLORS.cocoa });
    g.poly([-8, -2, 12, -2, -4, -38]).fill(COLORS.cream).stroke({ width: 4, color: COLORS.cocoa });
    g.poly([-8, 2, 12, 2, -4, 34]).fill(COLORS.cream).stroke({ width: 4, color: COLORS.cocoa });
    g.poly([-44, -2, -32, -2, -46, -22]).fill(COLORS.raspberry).stroke({ width: 3, color: COLORS.cocoa });
    g.circle(26, -3, 5).fill(COLORS.sky);
    c.addChild(g);
    const p = nodePos(Math.min(next, LEVELS.length));
    c.position.set(p.x > SCREEN.width / 2 ? p.x - 200 : p.x + 200, p.y - 150);
    c.rotation = -0.25;
    if (!reducedMotion) {
      gsap.to(c, { y: c.y - 24, duration: 1.4, yoyo: true, repeat: -1, ease: 'sine.inOut' });
      gsap.to(c, { rotation: -0.1, duration: 2.1, yoyo: true, repeat: -1, ease: 'sine.inOut' });
    }
    return c;
  }

  // ------------------------------------------------------------ scrolling

  private clamp(y: number): number {
    if (!this.l) return y;
    // content y range: top of map may scroll down to the header, home up to the footer
    const lo = this.l.safeBottom - 180 - this.maxY;
    const hi = this.l.safeTop + 200 - this.minY;
    return Math.min(hi, Math.max(lo, y));
  }

  private down(e: FederatedPointerEvent): void {
    gsap.killTweensOf(this.map);
    this.velocity = 0;
    this.drag = { y: e.global.y, mapY: this.map.y, moved: 0, t: performance.now() };
  }

  private move(e: FederatedPointerEvent): void {
    if (!this.drag) return;
    const s = this.view.worldTransform.d || 1;
    const dy = (e.global.y - this.drag.y) / s;
    const now = performance.now();
    const ny = this.clamp(this.drag.mapY + dy);
    this.velocity = (ny - this.map.y) / Math.max(1, now - this.drag.t);
    this.drag.t = now;
    this.drag.moved = Math.max(this.drag.moved, Math.abs(dy));
    this.map.y = ny;
  }

  private up(): void {
    if (!this.drag) return;
    const moved = this.drag.moved;
    setTimeout(() => (this.drag = undefined), 0);
    if (moved > 12 && Math.abs(this.velocity) > 0.05) {
      gsap.to(this.map, { y: this.clamp(this.map.y + this.velocity * 350), duration: 0.8, ease: 'power3.out' });
    }
  }

  layout(l: Layout): void {
    const first = !this.l;
    this.l = l;
    this.sea.clear().rect(l.left, 0, l.right - l.left, l.bottom).fill(COLORS.sky);
    this.view.hitArea = new Rectangle(l.left, 0, l.right - l.left, l.bottom);
    this.header.y = l.safeTop + 30;
    this.footer.y = l.safeBottom - 70;
    if (first) {
      // show the next level a little below the middle
      const next = Math.min(this.nav.save().nextLevel, LEVELS.length);
      this.map.y = this.clamp(l.bottom * 0.58 - nodePos(next).y);
    }
  }
}
